<script lang="ts" setup>
import {
    faCheck,
    faChevronRight,
    faCircleUser,
    faEdit,
    faSignOut,
} from "@fortawesome/pro-regular-svg-icons";
import { faEye } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useSettingsNav } from "~/utils/navigation";

const nuxtApp = useNuxtApp();
const { user, logout: sanctumLogout } = useAuth();
const { refresh: userRefresh } = useUser();
const loggingOut = ref<boolean>(false);

const { collapsed = false } = defineProps<{
    collapsed?: boolean;
}>();

const navigation = computed(() =>
    useSettingsNav(useI18n()).filter(
        (i) => i.items.filter((i) => !i.hideInUserDropdown).length,
    ),
);

async function logout() {
    loggingOut.value = true;
    await sanctumLogout();
    loggingOut.value = false;
}

const items = (close: () => void) => {
    return (user.value?.accounts ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((account) => ({
            label: account.name,
            badge: account.role?.name || undefined,
            active: account.id === user.value.account.id,
            trailingIcon:
                account.id === user.value.account.id ? faCheck : undefined,
            avatar: {
                src: account.logo,
                alt: account.name,
                size: "sm",
                class: "mr-2",
            },
            click: () => {
                close();

                if (account.id !== user.value.account.id) {
                    useApi().auth.account.swap(account.id.toString());
                    userRefresh();
                    refreshNuxtData();
                    useToast().add({
                        title: `Switched account to ${account.name}!`,
                    });
                }
            },
        }));
};
</script>

<template>
    <div
        :class="{
            'justify-center': collapsed,
        }"
        class="flex items-center"
    >
        <UPopover
            v-if="user"
            :popper="{
                placement: 'top',
                offsetDistance: 16,
            }"
            :ui="{
                base: '!overflow-visible focus:outline-none relative',
                rounded: 'rounded-xl',
                background: 'bg-hd-neutral-200',
            }"
            class="flex-1"
        >
            <UButton
                :class="{
                    '-m-2': !collapsed,
                    '!rounded-xl': collapsed,
                }"
                block
                class="justify-start group hover:!bg-hd-neutral-100 active:!bg-hd-neutral-200 p-2"
                color="hd-neutral"
                rounded
                size="md"
                variant="ghost"
            >
                <div class="flex gap-4">
                    <UAvatar
                        :alt="user.name"
                        :size="collapsed ? 'sm' : 'md'"
                        :src="user.avatar"
                    />
                    <UTooltip
                        v-if="user.current_partner_tenant && collapsed"
                        class="absolute bottom-0 transition duration-150 ease-in-out group-active:bg-hd-neutral-200 group-hover:bg-hd-neutral-100 bg-hd-neutral-50 p-0.5 h-5 w-5 flex items-center justify-center rounded-full right-0"
                        text="You're currently in a partner connected account"
                        :popper="{ placement: 'top' }"
                        ><FontAwesomeIcon :icon="faEye" size="xs"
                    /></UTooltip>
                    <div
                        v-if="!collapsed"
                        class="flex flex-col text-left text-hd-primary-original"
                    >
                        <span class="font-medium flex items-center gap-1"
                            >{{ user.name }}
                            <UTooltip
                                v-if="user.current_partner_tenant"
                                text="You're currently in a partner connected account"
                                :popper="{ placement: 'top' }"
                                ><FontAwesomeIcon :icon="faEye" /></UTooltip
                        ></span>
                        <span class="inline-flex gap-2 items-center">
                            <span
                                v-if="user.account"
                                class="text-sm font-normal truncate opacity-75 overflow-hidden w-full max-w-[7.5rem]"
                            >
                                {{ user.account.name }}
                            </span>
                            <UBadge
                                :label="
                                    $t(
                                        `labels.${user.role}`,
                                    ).toLocaleLowerCase()
                                "
                                :ui="{ rounded: 'rounded-full' }"
                                class="font-medium"
                                color="gray"
                                size="sm"
                            />
                        </span>
                    </div>
                </div>
            </UButton>
            <template #panel="{ close }">
                <div class="relative w-72 flex flex-col gap-4 p-2">
                    <div class="flex ml-4 z-10">
                        <UButton
                            :to="{ name: 'settings-me-profile' }"
                            class="hover:!bg-transparent active:!bg-transparent hover:[&>img]:brightness-75 active:[&>img]:brightness-75"
                            color="white"
                            square
                            variant="ghost"
                        >
                            <UIAvatar
                                :src="user.avatar"
                                class="rounded-full transition"
                                size="2xl"
                            />
                        </UButton>
                        <UButton
                            :to="{ name: 'settings-me-profile' }"
                            class="top-2 right-2 absolute"
                            color="hd-neutral"
                            square
                        >
                            <FontAwesomeIcon :icon="faEdit" />
                        </UButton>
                    </div>
                    <div
                        class="bg-hd-neutral-50 -mt-12 pt-10 rounded-xl px-4 pb-4 flex flex-col gap-4"
                    >
                        <div>
                            <div class="text-sm font-display font-medium">
                                {{ user.name }}
                            </div>
                            <div class="text-sm text-hd-neutral-700">
                                {{ user.account.name }} •
                                {{ $t(`labels.${user.role}`) }}
                            </div>
                        </div>
                        <div
                            v-for="list in navigation"
                            :key="list.label"
                            class="bg-white rounded-xl shadow-md p-1 relative"
                        >
                            <span
                                class="font-display text-sm font-medium opacity-50 px-2.5 -mb-1"
                                >{{ list.label }}</span
                            >
                            <template
                                v-for="item in list.items"
                                :key="item.label"
                            >
                                <UButton
                                    v-if="!item.hideInUserDropdown"
                                    :to="item.to"
                                    block
                                    color="gray"
                                    variant="ghost"
                                >
                                    <template #leading>
                                        <FontAwesomeIcon :icon="item.icon" />
                                    </template>
                                    <span class="text-left block flex-1">{{
                                        item.label
                                    }}</span>
                                </UButton>
                            </template>
                        </div>
                        <div class="bg-white rounded-xl shadow-md p-1 relative">
                            <HeadlessPopover
                                v-if="user.accounts.length > 1"
                                class="relative"
                                mode="hover"
                            >
                                <HeadlessPopoverButton as="template">
                                    <UButton block color="gray" variant="ghost">
                                        <template #leading>
                                            <FontAwesomeIcon
                                                :icon="faCircleUser"
                                            />
                                        </template>
                                        <span class="text-left block flex-1"
                                            >Switch Account</span
                                        >
                                        <template #trailing>
                                            <FontAwesomeIcon
                                                :icon="faChevronRight"
                                            />
                                        </template>
                                    </UButton>
                                </HeadlessPopoverButton>
                                <transition
                                    v-bind="{
                                        enterActiveClass:
                                            'ease-out duration-200',
                                        enterFromClass: 'opacity-0',
                                        enterToClass: 'opacity-100',
                                        leaveActiveClass:
                                            'ease-in duration-150',
                                        leaveFromClass: 'opacity-100',
                                        leaveToClass: 'opacity-0',
                                    }"
                                >
                                    <HeadlessPopoverPanel
                                        v-slot="panel"
                                        class="absolute left-full bottom-0 w-72 max-h-80 overflow-y-auto p-2"
                                    >
                                        <UiDropdownItems
                                            :items="[
                                                items(() => {
                                                    panel.close();
                                                    close();
                                                }),
                                            ]"
                                            :ui="{
                                                padding: 'p-2',
                                                item: {
                                                    padding: 'px-3 py-2',
                                                    selected: ' ',
                                                    label: 'flex-1 text-left font-medium',
                                                },
                                            }"
                                        />
                                    </HeadlessPopoverPanel>
                                </transition>
                            </HeadlessPopover>
                            <UButton
                                :loading="loggingOut"
                                block
                                color="gray"
                                variant="ghost"
                                @click="logout"
                            >
                                <template #leading>
                                    <FontAwesomeIcon :icon="faSignOut" />
                                </template>
                                <span class="text-left block flex-1"
                                    >Sign out</span
                                >
                            </UButton>
                        </div>
                    </div>
                </div>
            </template>
        </UPopover>
        <UTooltip
            v-if="!collapsed"
            :popper="{ placement: 'right' }"
            text="Sign out"
        >
            <UButton
                :disabled="loggingOut"
                :loading="loggingOut"
                class="items-center justify-center transform -translate-y-0.5"
                color="hd-neutral"
                size="md"
                square
                variant="ghost"
                @click="logout"
            >
                <FontAwesomeIcon :icon="faSignOut" size="xl" />
            </UButton>
        </UTooltip>
    </div>
</template>
